import { baseApi } from 'segments/api/baseApi';

import type { Attendance, LessonDebt, RescheduleDay, SubscriptionPlan, SubscriptionPlanInput, SubscriptionPurchase, UserProfile } from './types';

export const adminOperationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttendance: builder.query<Attendance, { bookingId: number; bookingKind: 'court-bookings' | 'coach-bookings' }>({
      query: ({ bookingId, bookingKind }) => `/admin/attendance/${bookingKind}/${bookingId}`,
      providesTags: ['Attendance', 'UsersList', 'GroupsList', 'CourtsBooking'],
    }),
    saveAttendance: builder.mutation<Attendance, { bookingId: number; bookingKind: 'court-bookings' | 'coach-bookings'; userIds: number[] }>({
      query: ({ bookingId, bookingKind, userIds }) => ({
        url: `/admin/attendance/${bookingKind}/${bookingId}`,
        method: 'PUT',
        body: { 'present_user_ids': userIds },
      }),
      invalidatesTags: ['Attendance', 'Subscriptions', 'UsersList', 'Transactions'],
    }),
    getRescheduleAvailability: builder.query<RescheduleDay[], { bookingId: number; dateFrom: string; days: number }>({
      query: ({ bookingId, dateFrom, days }) => ({
        url: `/admin/court_bookings/${bookingId}/reschedule/availability`,
        params: { 'date_from': dateFrom, days },
      }),
      providesTags: ['CourtsBooking', 'CoachesBooking'],
    }),
    rescheduleBooking: builder.mutation<void, { bookingId: number; startTime: string; courtId: number }>({
      query: ({ bookingId, startTime, courtId }) => ({
        url: `/admin/court_bookings/${bookingId}/reschedule`,
        method: 'POST',
        body: { 'start_time': startTime, 'court_id': courtId },
      }),
      invalidatesTags: ['CourtsBooking', 'CoachesBooking'],
    }),
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      query: () => '/admin/subscriptions/plans',
      providesTags: ['Subscriptions'],
    }),
    createSubscriptionPlan: builder.mutation<SubscriptionPlan, SubscriptionPlanInput>({
      query: (body) => ({ url: '/admin/subscriptions/plans', method: 'POST', body }),
      invalidatesTags: ['Subscriptions'],
    }),
    updateSubscriptionPlan: builder.mutation<SubscriptionPlan, { id: number; body: SubscriptionPlanInput }>({
      query: ({ id, body }) => ({ url: `/admin/subscriptions/plans/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Subscriptions'],
    }),
    deleteSubscriptionPlan: builder.mutation<void, number>({
      query: (id) => ({ url: `/admin/subscriptions/plans/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Subscriptions'],
    }),
    getSubscriptionPurchases: builder.query<SubscriptionPurchase[], void>({
      query: () => '/admin/subscriptions/purchases',
      providesTags: ['Subscriptions', 'UsersList', 'Transactions'],
    }),
    purchaseSubscription: builder.mutation<SubscriptionPurchase, { idempotency_key: string; user_id: number; plan_id: number; purchased_on: string; payment_confirmed: true }>({
      query: (body) => ({ url: '/admin/subscriptions/purchases', method: 'POST', body }),
      invalidatesTags: ['Subscriptions', 'UsersList'],
    }),
    getLessonDebts: builder.query<LessonDebt[], boolean>({
      query: (includeSettled) => ({
        url: '/admin/subscriptions/debts',
        params: { 'include_settled': includeSettled },
      }),
      providesTags: ['Subscriptions', 'UsersList', 'Transactions'],
    }),
    settleLessonDebt: builder.mutation<void, number>({
      query: (id) => ({ url: `/admin/subscriptions/debts/${id}/settle`, method: 'PUT', body: { note: null } }),
      invalidatesTags: ['Subscriptions', 'UsersList'],
    }),
    reopenLessonDebt: builder.mutation<void, number>({
      query: (id) => ({ url: `/admin/subscriptions/debts/${id}/reopen`, method: 'PUT', body: { note: null } }),
      invalidatesTags: ['Subscriptions', 'UsersList'],
    }),
    getUserProfile: builder.query<UserProfile, number>({
      query: (id) => `/admin/users/profile/${id}`,
      providesTags: ['UsersList', 'Subscriptions', 'Transactions', 'CourtsBooking'],
    }),
  }),
});

export const {
  useGetAttendanceQuery,
  useSaveAttendanceMutation,
  useGetRescheduleAvailabilityQuery,
  useRescheduleBookingMutation,
  useGetSubscriptionPlansQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation,
  useGetSubscriptionPurchasesQuery,
  usePurchaseSubscriptionMutation,
  useGetLessonDebtsQuery,
  useSettleLessonDebtMutation,
  useReopenLessonDebtMutation,
  useGetUserProfileQuery,
} = adminOperationsApi;
